import { useContext, useEffect, useRef } from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { ExperienceState } from '@confluence/experience-tracker/entry-points/Experience.ts';
import {
	BYLINE_CONNECTIONS_LOAD_EXPERIENCE,
	EDIT_PAGE_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

export const useBylineConnectionsLoadTiming = (
	contentId: string,
	isResolving: boolean,
	hasConnection: boolean,
	error?: Error | null,
): void => {
	const tracker = useContext(ExperienceTrackerContext);
	const activeLoad = useRef<string | undefined>(undefined);
	// Match the page before using a clock retained from an earlier route.
	let pageExperience: ExperienceState | undefined;
	for (const name of [VIEW_PAGE_EXPERIENCE, EDIT_PAGE_EXPERIENCE]) {
		const state = tracker.getExperienceState(name);
		if (
			state &&
			(state.taskId === contentId || state.attributes?.contentId === contentId) &&
			(!pageExperience || state.startTime > pageExperience.startTime)
		) {
			pageExperience = state;
		}
	}
	const pageExperienceName = pageExperience?.name;
	const startTime = pageExperience?.startTime;
	const loadId = pageExperience
		? `${contentId}:${pageExperience.name}:${pageExperience.startTime}`
		: undefined;

	useEffect(() => {
		const name = BYLINE_CONNECTIONS_LOAD_EXPERIENCE;
		activeLoad.current = loadId;
		if (!loadId || startTime === undefined) {
			return;
		}

		if (tracker.getExperienceState(name)?.taskId !== loadId) {
			tracker.start({
				name,
				id: loadId,
				startTime,
				attributes: {
					pageExperience: pageExperienceName,
					errorAttribution: Attribution.INTEGRATIONS,
				},
			});
		}

		if (!isResolving) {
			if (error) {
				tracker.stopOnError({ name, error });
			} else {
				tracker.succeed({
					name,
					attributes: { hasConnection },
				});
			}
		}

		return () => {
			activeLoad.current = undefined;
			// Let StrictMode restore the effect before treating cleanup as navigation.
			void Promise.resolve().then(() => {
				const state = tracker.getExperienceState(name);
				if (activeLoad.current !== loadId && state?.taskId === loadId) {
					tracker.abort({
						name,
						reason: 'Byline connections unmounted before resolution',
					});
				}
			});
		};
	}, [error, tracker, hasConnection, isResolving, loadId, pageExperienceName, startTime]);
};
